<template>
  <div>
    <div v-show="!hasSelection" class="title-bar flex-wrap py-3 align-items-center">
      <h3 class="mb-0">{{title}}</h3>

      <div class="ml-auto"></div>
      <slot name="toolbar"
        :enable-exif="enableExif"
        :update-enable-exif="val => enableExif = val"
        :enable-validation="enableValidation"
        :update-enable-validation="val => enableValidation = val"
      />

      <file-upload
        ref="upload"
        name="upload-images"
        v-model="images"
        accept=".jpeg,.jpg,.png,.csv"
        multiple
        :drop="true"
        :drop-directory="true"
        :custom-action="uploadImage"
        @input-filter="inputFilter"
        @before-input="onBeforeInput"
        @after-input="onAfterInput"
        :disabled="inputLoading"
        class="btn btn-outline-primary pointer ml-2"
        :class="{disabled: inputLoading}"
      >
      Choose files
      </file-upload>
    </div>

    <b-alert dismissible :show="!hasWorkers" variant="warning">Exif import is not supported in this browser.</b-alert>

    <!--
    <div class="text-muted">
      Debug:
      &bull; {{combinedData.length}} rows
      <span v-if="csvFile">
        &bull;
        ({{csvData.length}} from {{csvFile.name}},
        {{activeStubs.length}} others)
      </span>
      &bull; active: {{$refs.upload && $refs.upload.active}}
    </div>
    -->

    <div
      v-if="csvFile || combinedData.length || loading"
    >
      <EditBatch
        :data="combinedData"
        :metadata="metadataFiltered"
        :images="images"
        :busy="loading"
        @remove="onRemove"
        :has-selection.sync="hasSelection"
        class="min-vh-100"
      >
        <template #info>
          <span v-if="stubsLoading" class="px-2 text-tabular">
            <ThrottleRender key="stubs" :get-value="() => stubsLoaded"/> / {{stubsToLoad}} imported
          </span>
          <span v-if="errorsLoading" class="px-2 text-tabular">
            <ThrottleRender key="errors" :get-value="() => errorsLoaded"/> / {{errorsToLoad}} validated
          </span>
          <span v-if="csvFile" class="ml-2">
            <IconFile
              :title="`${csvFile.name} (${formatCount(csvData.length, 'row')})`"
              v-b-tooltip.hover
              class="mt-n1"
            />
          </span>
        </template>
      </EditBatch>
    </div>
    <label
      v-else
      for="upload-images"
      class="dropzone"
    >
      Drag files here to upload
    </label>

    <div
      v-show="$refs.upload && $refs.upload.dropActive"
      @click="$refs.upload.dropActive = false"
      class="overlay dropzone-overlay"
    >
      <h3>Drop files to upload</h3>
    </div>

    <div class="sticky-bottom frosted eat-gutter py-3 bottom-bar"
      v-if="combinedData.length"
    >
    <form class="form-inline align-items-center justify-content-end gutters-y-2"
      @submit.stop.prevent="onStart"
    >
      <slot name="bottom-bar"/>
      <div v-if="errorText" class="ml-3 text-danger text-right">
        {{errorText}}
      </div>
      <div class="ml-3">
        <button
          type="submit"
          class="btn btn-primary"
          :disabled="loading || errorCount || missingImageCount || !images.length"
        >
          Start upload <IconChevronRight class="chevron"/>
        </button>
      </div>
    </form>
    </div>
    <div class="mb-bar"></div><!-- hold space for fixed position sticky-bottom (IE) -->

    <div
      v-show="locked || ($refs.upload && $refs.upload.active)"
      class="overlay locked-overlay"
    >
      <div class="bg-light rounded sm-box position-relative" :class="{enlarge: $refs.upload && $refs.upload.active}">
        <transition name="fade">
          <div v-if="$refs.upload && $refs.upload.active" key="uploading" class="centered flex-column align-items-stretch">
            <b-progress :value="Math.max(imagesUploaded, 0.1)" :max="imagesAmount" animated class="m-3"/>
            <div>Uploading {{imagesUploaded+1}}/{{imagesAmount}}</div>
          </div>
          <div v-else key="processing" class="centered flex-column">
            <b-spinner small class="m-3" />
            <div>Processing</div>
          </div>
        </transition>
      </div>
    </div>

  </div>
</template>

<script>
import VueUploadComponent from '@/features/SmartUpload/SmartUpload.js'
import { get, identity, partition, pickBy } from 'lodash'

import EditBatch from '@/components/EditBatch.vue'
import { hasDataErrors, hasMissingImage } from '@/components/EditBatchFilters.vue'
import UploadImageMinSize from '@/components/UploadImageMinSize.js'
import ThrottleRender from '@/components/ThrottleRender.vue'
import IconChevronRight from '@/images/chevron-right.svg'
import IconFile from '@primer/octicons/build/svg/file-16.svg'
import { extraKeys } from '@/components/OptionsFormFields.vue'
import { accumulateMessage } from '@/services/accumulateMessage.js'
import { accumulateBatch } from '@/services/accumulateBatch.js'
import { http } from '@/services/http.js'
import { ExifWorkerPool } from "@/services/exif-worker-pool.js"
import { alignChat } from '@/utils/chat.js'
import { formatCount } from '@/utils/count.js'
import { extension } from '@/utils/extension.js'
import { omit } from '@/utils/omit.js'

const URL = window.URL || window.webkitURL

export default {
  name: 'UploadBulk',
  components: {
    FileUpload: VueUploadComponent,
    IconChevronRight,
    IconFile,
    EditBatch,
    ThrottleRender,
  },
  mixins: [
    UploadImageMinSize,
  ],
  props: {
    title: {
      type: String,
      default: 'Upload',
    },
    mode: {
      type: String,
      default: 'artist',
    },
    getUpload: Function,
    omitFields: {
      type: Array,
      default: () => [],
    },
    omitFieldsCsv: {
      type: Array,
      default: () => [],
    },
    getId: {
      type: Function,
      default: identity,
    },
  },
  data() {
    return {
      csvFile: null,
      csvLoading: false,
      inputLoading: false,
      stubsLoading: false,
      stubsLoaded: 0,
      stubsToLoad: 0,
      errorsLoading: false,
      csv: null,
      metadata: null,
      uploadObj: null,
      locked: false,
      images: [],
      stubs: [],
      hasSelection: false,
      enableExif: true,
      enableValidation: true,
      addStubsPromise: Promise.resolve(),
      workerPool: new ExifWorkerPool(2),
    }
  },
  computed: {
    loading() {
      return this.csvLoading || this.inputLoading || this.stubsLoading || this.errorsLoading
    },
    errorsLoaded() {
      return this.images.filter(i => i.dimensions).length
    },
    errorsToLoad() {
      return this.images.length
    },
    activeStubs() {
      return this.stubs
        .filter(stub => {
          return !this.csvData.some(i => i.image_name === stub.image_name)
        })
    },
    csvData() {
      return (this.csv && this.csv.data) || []
    },
    combinedData() {
      return [
        ...this.activeStubs,
        ...this.csvData,
      ]
    },
    errorCount() {
      return this.combinedData.filter(hasDataErrors).length
    },
    missingImageCount() {
      return this.combinedData.filter(hasMissingImage).length
    },
    errorText() {
      return [
        this.errorCount ? formatCount(this.errorCount, 'error') : null,
        this.missingImageCount ? formatCount(this.missingImageCount, 'missing image') : null,
      ].filter(i => i).join(', ')
    },
    substrates() {
      const choices = get(this.metadata, 'substrates.choices') || []
      return choices.map(i => i.value)
    },
    imagesAmount() {
      return this.images.length
    },
    imagesUploaded() {
      return this.images.filter(i => i.success || i.error).length
    },
    metadataFiltered() {
      const fields = (this.csvFile && this.omitFieldsCsv) ? this.omitFieldsCsv : this.omitFields
      return omit(this.metadata, fields)
    },
    hasWorkers() {
      return this.workerPool.workers?.length
    },
  },
  watch: {
    mode: 'onChangeMode',
    'combinedData.length'(val) {
      alignChat(val ? 'up' : null)
    },
    enableExif: 'onEnableExif',
  },
  created() {
    this.setMetadata()
      .catch(err => {
        this.$store.dispatch('errors/setError', err)
        throw err
      })
  },
  mounted() {
    this.$watch('$refs.upload.uploaded', this.onUploaded)
  },
  destroyed() {
    revokeObjectURLs(this.images)
    this.workerPool.terminate()
    alignChat(null)
  },
  methods: {
    formatCount,
    async onStart() {
      this.locked = true
      try {
        this.uploadObj = await this.getUpload()
        await this.validateImages()
      } finally {
        this.locked = false
      }
      this.$refs.upload.active = true
    },
    onUploaded(isUploaded) {
      const numImages = this.images.length
      const numErrors = this.images.filter(i => i.error).length
      if (isUploaded && numImages) {
        const errorsText = numErrors ? `${formatCount(numErrors, 'error')}.` : ''
        this.$bvModal.msgBoxOk(`${numImages} images uploaded. ${errorsText}`, {
          title: 'Upload complete',
          size: 'sm',
          centered: true,
        })
        .then(() => {
          this.$emit('success', this.uploadObj)
        })
      }
    },
    onChangeMode() {
      if (this.csvFile) {
        this.setCSV(this.csvFile)
      } else {
        this.setMetadata()
          .catch(err => {
            this.$alert.simple(`${err}\nPlease contact customer support if this persists.`, {variant: 'danger'})
            throw err
          })
      }
    },
    onRemove(selected) {
      const names = selected.map(i => i.image_name)

      if (this.csv && this.csv.data) {
        this.csv.data = this.csv.data.filter(i => !names.includes(i.image_name))
        if (this.csv.data.length === 0) {
          this.csvFile = null
          this.csv = null
        }
      }
      this.stubs = this.stubs.filter(i => !names.includes(i.image_name))
      const [images, removed] = partition(this.images, i => !names.includes(getName(i)))
      this.images = images
      revokeObjectURLs(removed)
    },
    async setCSV(file) {
      this.csv = await this.getCSV(file, this.mode)
      this.metadata = this.csv.metadata
    },
    getCSV(file, mode) {
      this.csvLoading = true
      const formData = new FormData()
      formData.append('csv_file', file)
      return http.post(
        `/api/csv/validate-${mode}-upload/`,
        formData,
        { headers: {'Content-Type': 'multipart/form-data'} }
      ).then(response => {
        if (!response.data.data) { throw new Error('Malformed data from API') }
        return {...response.data, data: response.data.data.map(i => {
          return {...i, _cellVariants: {
            ...i._cellVariants,
            // add "missing image" errors
            thumb: 'danger',
          }, errors: {
            ...i.errors,
            thumb: 'Missing image',
          },
            // show errors that do not correspond to a visible column
            ...(this.hasInvisibleErrors(i.errors) ? {
              _rowVariant: 'danger',
              _showDetails: true,
            } : {}),
          }
        })}
      }).catch(err => {
        const msg1 = err?.response?.data?.detail ?? err
        let msg2 = 'Please contact customer support.'
        if (msg1.includes("CSV parse error - 'utf-8' codec can't decode")) {
          msg2 = 'To fix this, “Save As” in Excel and choose “CSV UTF-8” as the File Format. Please contact customer support if this does not help.'
        }
        this.$alert.persistent(`${msg1}\n${msg2}`, {variant: 'danger'})
        throw err
      }).finally(() => {
        this.csvLoading = false
      })
    },
    async setMetadata() {
      this.metadata = await this.getMetadata(this.mode)
    },
    getMetadata(mode) {
      return http.options(
        `/api/csv/validate-${mode}-upload/`
      ).then(response => {
        return response.data
      })
    },
    validateImages() {
      return http.post(
        '/api/images/validate/',
        this.combinedData.map(i => prepareRow(i, this.uploadObj.id, this.substrates))
      ).catch(err => {
        let message
        if (err.response && err.response.status === 400) {
          const count = err.response.data.filter(i => Object.keys(i).length).length
          message = `${count} images with errors`
          this.processValidationErrors(err.response.data)
        } else {
          message = `${err}`
        }
        this.$alert.simple(message, {variant: 'danger'})
        throw err
      })
    },
    uploadImage(file) {
      let row = this.combinedData.find(i => {
        return i.image_name === getName(file)
      })
      row = prepareRow(row, this.uploadObj.id, this.substrates)
      const formData = new FormData()
      formData.append('data', JSON.stringify(row))
      formData.append('image', file.file)
      return http.post(
        '/api/images/upload/',
        formData,
        { headers: {'Content-Type': 'multipart/form-data'} }
      ).then(response => {
        return response.data
      }).catch(err => {
        this.$refs.upload.active = false
        this.$alert.simple(`Upload failed: ${file.file.name}`, {variant: 'danger'})
        throw err
      })
    },
    processValidationErrors(data) {
      for (let i=0; i<this.activeStubs.length; i++) {
        this.applyValidationErrors(this.activeStubs[i], data[i])
      }
      for (let i=0; i<this.csvData.length; i++) {
        this.applyValidationErrors(this.csvData[i], data[i + this.activeStubs.length])
      }
    },
    applyValidationErrors(item, errors) {
      if (Array.isArray(errors) || this.hasInvisibleErrors(errors)) {
        this.$set(item, '_showDetails', true)
        this.$set(item, '_rowVariant', 'danger')
      } else {
        this.$set(item, '_cellVariants', transformError(errors))
      }
      this.$set(item, 'errors', errors)
    },
    hasInvisibleErrors(data) {
      return extraKeys(this.metadataFiltered, data)
    },
    onBeforeInput() {
      this.inputLoading = true
    },
    onAfterInput(result) {
      // If there are no results, loading is done. Otherwise, inputLoading will end when stubsLoading begins.
      if (!result) {
        this.inputLoading = false
      }
    },
    /**
     * Pretreatment
     * @param  Object|undefined   newFile   Read and write
     * @param  Object|undefined   oldFile   Read only
     * @param  Function           prevent   Prevent changing
     * @return undefined
     */
    inputFilter: function (newFile, oldFile, prevent) {
      if (newFile && !oldFile) {
        // add file

        // remove any persistent alerts
        this.$alert.reset()

        // peel off csv files
        if (isCSV(newFile.file)) {
          this.csvFile = newFile.file
          this.csv = null
          this.setCSV(newFile.file)
            .catch(() => this.csvFile = null)
          return prevent()
        }
        // Filter junk files
        if (isJunk(newFile.file)) {
          return prevent()
        }
        // Filter non-image file
        if (!isImage(newFile.file)) {
          accumulateMessage('Invalid file format (## files). Upload jpeg, png, or csv files.\n', newFile.file.name, (msg) => this.$alert.simple(msg))
          return prevent()
        }
        // Remove duplicates
        const newFileName = getName(newFile)
        this.images
          .filter(i => getName(i) === newFileName)
          .forEach(i => {
            this.$refs.upload.remove(i)
            revokeObjectURLs([i])
            accumulateMessage('Duplicates detected. Replacing ## files:\n', i.file.name, (msg) => this.$alert.simple(msg))
          })

        // Create a blob field
        newFile.blob = ''
        if (URL && URL.createObjectURL) {
          newFile.blob = URL.createObjectURL(newFile.file)
        }

        // make properties reactive
        newFile.dimensions = null

        // Add stub data
        accumulateBatch('stubs', newFile, (files) => {
          this.addStubsPromise = this.addStubsPromise.then(() => this.addStubs(files))
        })
      }
    },
    async getExif(file) {
      const { dimensions, ...exif } = await this.workerPool.getExif(file.file)
        .then(data => data || {})
        .catch((err) => accumulateMessage(`${err} (## files)\n`, file.file.name, (msg) => this.$alert.simple(msg)))
      if (dimensions) {
        file.dimensions = dimensions // make available for file validation (UploadImageMinSize.js)
      }
      return exif
    },
    getStub(file, exif) {
      const name = getName(file)
      this.stubsLoaded++
      const stub = {
        image_name: name,
        unique_id: this.getId(name),
        ...exif,
      }
      return stub
    },
    async getStubs(files) {
      if (!this.enableExif) {
        return files.map(i => this.getStub(i))
      }
      try {
        // Cancellable exif import
        return await Promise.race([
          new Promise((resolve, reject) => {
            this.cancelExif = (err = new Error('Exif import cancelled')) => {
              this.workerPool.terminate()
              this.workerPool = new ExifWorkerPool(2)
              reject(err)
            }
          }),
          Promise.all(files.map(async (file) => {
            return this.getStub(file, await this.getExif(file))
          })),
        ])
      } catch {
        return files.map(i => this.getStub(i))
      } finally {
        this.cancelExif = undefined
      }
    },
    addStub(stub, stubsNames) {
      const existing = this.stubs[stubsNames.indexOf(stub.image_name)]
      if (existing) {
        Object.assign(
          existing,
          {errors: undefined, _cellVariants: undefined, _rowVariant: undefined, _showDetails: undefined},
          pickBy(stub, i => i !== undefined)
        )
      } else {
        this.stubs.push(stub)
      }
    },
    async addStubs(files) {
      this.stubsLoaded = 0
      this.stubsToLoad = files.length
      this.stubsLoading = true
      this.inputLoading = false

      // keep only the first instance of each file name, per drag (different extensions, subfolders, etc)
      const [unique, removed] = partitionUnique(files, getName)
      if (removed.length) {
        files = unique
        const removedNames = removed.map(i => i.name).join(', ')
        this.$alert.simple(`Duplicate file names. Ignoring ${removed.length} files:\n${removedNames}`)
        removed.forEach(i => this.$refs.upload.remove(i))
        revokeObjectURLs(removed)
      }

      // - Dragging a folder from Finder results in a list of files that are
      // sorted unexpectedly. The order seems consistent, but it is not by name,
      // date, or any other criteria I checked. This is confusing, so sort by
      // name instead.
      // - Sort on the file upload object name because it is a path e.g.
      // "folder/file.jpg". This gives us a sort that keeps files grouped by
      // folder, in the case of multiple folders or subfolders.
      // - Only sort when there is a folder involved, because otherwise, files
      // come in sorted based on the sorting in Finder, which is desirable.
      // - Known edge cases like 'f 2/b.jpg' sorting before 'f/a.jpg', but
      // more correctness requires more complexity
      const includesDirectory = files.some(i => i.name.includes('/'))
      if (includesDirectory) {
        files.sort((a, b) => a.name.localeCompare(b.name, undefined, {numeric: true}))
      }

      try {
        const results = await this.getStubs(files)
        const stubsNames = this.stubs.map(i => i.image_name)
        results.forEach(i => this.addStub(i, stubsNames))

        this.sortImages()
      } finally {
        this.stubsLoading = false
      }

      this.startValidation()
    },
    startValidation() {
      // trigger file validation (UploadImageMinSize.js)
      if (this.debouncedOnImages && this.enableValidation) {
        this.errorsLoading = true // prevent upload during debounce delay
        this.debouncedOnImages()
      }
    },
    sortImages() {
      // Sort images to match displayed order, so upload progress makes sense
      const imagesNames = this.images.map(getName)
      const sorted = this.combinedData.map(stub => {
        const index = imagesNames.indexOf(stub.image_name)
        return this.images[index]
      })
      this.images.sort((a, b) => sorted.indexOf(a) - sorted.indexOf(b))
    },
    async onEnableExif() {
      if (this.cancelExif) {
        this.cancelExif()
      }
      await this.addStubsPromise?.catch(() => {}) // wait for sortImages
      this.stubsLoaded = 0
      this.stubsToLoad = this.images.length
      if (!this.stubsToLoad) { return }
      this.stubsLoading = true
      try {
        this.stubs = await this.getStubs(this.images)
      } finally {
        this.stubsLoading = false
      }
      this.startValidation()
    },
  },
}

function isCSV(file) {
  return /\.(csv)$/i.test(file.name)
}
function isImage(file) {
  return /\.(jpeg|jpg|png)$/i.test(file.name)
}
function isJunk(file) {
  return /\.DS_Store$/i.test(file.name)
}
function prepareRow(obj, uploadId, allSubstrates) {
  const substrates = tokenize(obj.substrates)
  return {
    ...obj,
    upload: uploadId,
    substrates: substrates?.length ? substrates : allSubstrates,
    keywords: tokenize(obj.keywords),
  }
}
function tokenize(str) {
  if (!str) { return }
  if (Array.isArray(str)) { return str }
  return (str).split(/ *[,;\n] */).filter(i => i !== '')
}

function transformError(error) {
  return Object.keys(error).reduce((acc, key) => {
    acc[mapKey(key)] = 'danger'
    return acc
  }, {})
}
function mapKey(key) {
  const keys = {
    caption: 'description',
  }
  return keys[key] || key
}

function revokeObjectURLs(files) {
  files?.forEach(file => {
    URL?.revokeObjectURL?.(file.blob)
  })
}

function getName(item) {
  return extension(item.file.name, false)
}

function partitionUnique(items, fn = i=>i) {
  return items.map(fn).reduce((acc, item, index, arr) => {
    const isFirstInstance = arr.indexOf(item) === index
    acc[isFirstInstance ? 0 : 1].push(items[index])
    return acc
  }, [[],[]])
}
</script>

<style lang="scss" scoped>
  .btn-primary {
    fill: white;
  }
  .chevron {
    width: 0.75em;
    height: 0.75em;
    transform: translate(0, -0.05em);
  }

.title-bar {
  display: flex;
}
.bottom-bar {
  z-index: 3;
}

.gutters-y-2 {
  $gutter: 0.5rem;
  margin-top: -$gutter;
  > * {
    margin-top: $gutter;
  }
}

.dropzone {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 300px;
  border: 4px dashed $gray-400;
  transition: all 0.2s;
  &:active  {
    background-color: $gray-100;
    border-color: $gray-500;
  }
}
.overlay {
  display: flex;
  align-items: center;
  justify-content: center;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  position: fixed;
  z-index: $zindex-modal-backdrop;
  text-align: center;
}
.dropzone-overlay {
  background-color: rgba(black, 0.6);
  &, /deep/ > * {
    color: white;
  }
}

.locked-overlay {
  background-color: rgba($modal-backdrop-bg, $modal-backdrop-opacity);
}
.sm-box {
  width: 140px;
  height: 140px;
  transition: all 0.4s $easeInOutBack;
  &.enlarge {
    width: 300px;
    height: 140px;
    max-width: 100%;
  }
}

/deep/ .tooltip-inner {
  max-width: 300px;
}
</style>
